import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  ArrowLeft,
  Download,
  Check,
  Copy,
} from "lucide-react";
import { projects } from "../data/projects";
import { experience, education, tools, ResumeRow } from "../data/resume";
import { Benchmark } from "./Benchmark";
import { ExperienceIndex } from "./ExperienceIndex";
import { QualityEvidence, ForecastEvidence } from "./CaseEvidence";

export const email = "maxzrp24@gmail.com";
const resumePdf = "/resumes/Max-Zhang-ML-AI.pdf";
const repo = "https://github.com/maxz101524/7_SmartEats";

/** Renders ==phrase== as a highlighted <mark>. */
export const Marked: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text
      .split("==")
      .map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : part))}
  </>
);

const External: React.FC<{
  href: string;
  children: React.ReactNode;
  className?: string;
}> = ({ href, children, className = "text-link" }) => (
  <a className={className} href={href} target="_blank" rel="noreferrer">
    {children}
    <ArrowUpRight size={15} aria-hidden="true" />
  </a>
);

const CopyEmail: React.FC<{ className?: string }> = ({ className = "" }) => {
  const [state, setState] = useState<"idle" | "copied" | "error">("idle");
  async function copy() {
    try {
      await navigator.clipboard.writeText(email);
      setState("copied");
      window.setTimeout(() => setState("idle"), 2400);
    } catch {
      setState("error");
    }
  }
  return (
    <span className={`copy-email ${className}`}>
      <a href={`mailto:${email}`}>{email}</a>
      <button onClick={copy} aria-label="Copy email address">
        {state === "copied" ? (
          <Check size={15} aria-hidden="true" />
        ) : (
          <Copy size={15} aria-hidden="true" />
        )}
      </button>
      <span className="copy-status" role="status">
        {state === "copied"
          ? "Copied"
          : state === "error"
            ? "Select the address to copy it"
            : ""}
      </span>
    </span>
  );
};

export const Header: React.FC = () => (
  <header className="site-header">
    <div className="shell nav-inner">
      <Link className="wordmark" to="/" aria-label="Max Zhang, home">
        Max Zhang
      </Link>
      <nav className="navigation" aria-label="Main navigation">
        <Link to="/#work">Work</Link>
        <Link className="nav-optional" to="/#about">
          About
        </Link>
        <Link className="nav-optional" to="/#contact">
          Contact
        </Link>
        <a className="nav-resume" href={resumePdf}>
          Resume <Download size={14} aria-hidden="true" />
        </a>
      </nav>
    </div>
  </header>
);

const Row: React.FC<{ row: ResumeRow }> = ({ row }) => (
  <li className="cv-row">
    <span className="cv-when">
      {row.year}
      {row.months && <small>{row.months}</small>}
    </span>
    <span className="cv-org">{row.org}</span>
    <div className="cv-what">
      <strong>{row.title}</strong>
      {row.detail && (
        <p>
          <Marked text={row.detail} />
          {row.caseStudy && (
            <>
              {" "}
              <Link className="cv-link" to={`/work/${row.caseStudy}`}>
                Case study&nbsp;
                <ArrowRight size={13} aria-hidden="true" />
              </Link>
            </>
          )}
        </p>
      )}
    </div>
    {row.note && <span className="note">{row.note}</span>}
  </li>
);

const Pipeline: React.FC = () => (
  <div
    className="pipeline"
    aria-label="Migration reconciliation: 29,356 source objects, 28,727 copied, 627 duplicates, 2 other skips"
  >
    <div className="pipeline-source">
      <span>Source inventory</span>
      <strong>29,356</strong>
      <small>objects / 78.46 GiB</small>
    </div>
    <div className="pipeline-branches">
      <div>
        <span className="branch-dot" />
        <span>Copied</span>
        <strong>28,727</strong>
      </div>
      <div>
        <span className="branch-dot muted-dot" />
        <span>Duplicates</span>
        <strong>627</strong>
      </div>
      <div>
        <span className="branch-dot pale-dot" />
        <span>Other skips</span>
        <strong>2</strong>
      </div>
    </div>
    <p>
      <Check size={14} aria-hidden="true" /> Every source object accounted for
    </p>
  </div>
);

const Forecast: React.FC = () => (
  <figure className="forecast">
    <figcaption>
      Validation error <span>MAPE, lower is better</span>
    </figcaption>
    <div className="forecast-row">
      <span>Before</span>
      <div>
        <i style={{ width: "93%" }} />
      </div>
      <strong>9.3%</strong>
    </div>
    <div className="forecast-row">
      <span>After</span>
      <div>
        <i style={{ width: "56%" }} />
      </div>
      <strong>5.6%</strong>
    </div>
    <p>Rolling-window backtesting across 3 pilot sites</p>
  </figure>
);

const Screenshot: React.FC<{ alt: string; lazy?: boolean }> = ({
  alt,
  lazy,
}) => (
  <img
    src="/images/smarteats.webp"
    alt={alt}
    width="1832"
    height="1396"
    loading={lazy ? "lazy" : undefined}
  />
);

const caseVisual: Record<string, React.ReactNode> = {
  cloudsway: <Pipeline />,
  equota: <Forecast />,
  smarteats: (
    <Screenshot
      lazy
      alt="SmartEats application: menu discovery, AI meal planning and a nutrition profile"
    />
  ),
};

const caseWhen: Record<string, string> = {
  cloudsway: "Summer 2026",
  smarteats: "2025–present",
  equota: "2024–2025",
};

export const Home: React.FC = () => (
  <>
    <section className="intro shell" aria-labelledby="intro-title">
      <h1 id="intro-title">Max Zhang</h1>
      <figure className="intro-photo">
        <img
          src="/images/profile.jpg"
          alt="Max Zhang sitting on a railing at a street corner at night"
          width="800"
          height="925"
        />
      </figure>
      <div className="intro-body">
        <p className="intro-line">
          Data science &amp; applied AI. M.S.E. student at Penn, graduating
          December 2027.
        </p>
        <p className="intro-text">
          The work I enjoy most is the careful part: getting a dataset ready for
          someone else to use, and checking that a result holds up before anyone
          relies on it. I’m looking for a{" "}
          <strong>Summer 2027 internship</strong> in data science, machine
          learning, or applied AI.
        </p>
        <div className="intro-actions">
          <a className="button" href={resumePdf}>
            Resume (PDF) <Download size={15} aria-hidden="true" />
          </a>
          <CopyEmail />
          <span className="intro-social">
            <External href="https://www.linkedin.com/in/max-w-zhang/">
              LinkedIn
            </External>
            <External href="https://github.com/maxz101524">GitHub</External>
          </span>
        </div>
      </div>
    </section>

    <section
      id="experience"
      className="section shell"
      aria-labelledby="short-title"
    >
      <div className="section-label">
        <h2 id="short-title">The short version</h2>
        <p>The résumé at a glance. Case studies follow.</p>
      </div>
      <div className="cv">
        <div className="cv-group">
          <h3>Experience</h3>
          <ol>
            {experience.map((r) => (
              <Row key={r.year + r.org} row={r} />
            ))}
          </ol>
        </div>
        <div className="cv-group">
          <h3>Education</h3>
          <ol>
            {education.map((r) => (
              <Row key={r.year + r.org} row={r} />
            ))}
          </ol>
        </div>
        <div className="cv-group">
          <h3>Tools</h3>
          <ul>
            {tools.map((t) => (
              <li className="cv-row cv-tools" key={t.area}>
                <span className="cv-when" />
                <span className="cv-org">{t.area}</span>
                <p className="cv-what">{t.items}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section id="work" className="section shell" aria-labelledby="work-title">
      <div className="section-label">
        <h2 id="work-title">The longer version</h2>
        <p>Three projects: the problem, what I did, and what came of it.</p>
      </div>
      <div className="case-list">
        {projects.map((p, i) => (
          <article className="case-item" key={p.id}>
            <div className="case-copy">
              <p className="case-index">
                {String(i + 1).padStart(2, "0")}
                <span>
                  {p.name}, {caseWhen[p.id]}
                </span>
              </p>
              <h3>
                <Link to={`/work/${p.id}`}>{p.title}</Link>
              </h3>
              <p>{p.summary}</p>
              <p className="case-stack">{p.stack.join(" · ")}</p>
              <Link className="text-link" to={`/work/${p.id}`}>
                Read the case study <ArrowRight size={15} aria-hidden="true" />
              </Link>
            </div>
            <Link
              to={`/work/${p.id}`}
              className={`case-figure case-figure--${p.id}`}
              aria-hidden="true"
              tabIndex={-1}
            >
              {caseVisual[p.id]}
            </Link>
          </article>
        ))}
      </div>
    </section>

    <ExperienceIndex />

    <section id="about" className="section shell" aria-labelledby="about-title">
      <div className="section-label">
        <h2 id="about-title">Outside the résumé</h2>
      </div>
      <div className="about-grid">
        <div>
          <h3>How I work with AI</h3>
          <p>
            I use AI coding tools throughout development. My responsibility is
            to define the problem, inspect the implementation, test the result,
            and decide what is ready to use. The case studies show where that
            judgment mattered.
          </p>
        </div>
        <div>
          <h3>Away from the desk</h3>
          <p>
            I’m a powerlifter. Training and nutrition are part of my life
            outside school, and a useful connection to the problems behind
            SmartEats.
          </p>
          <External href="https://www.instagram.com/movingheavystuff/">
            Training at @movingheavystuff
          </External>
        </div>
        <div>
          <h3>Making videos</h3>
          <p>
            Since 2020 I’ve made educational and entertaining videos on
            Bilibili, a Chinese video platform, including sponsored videos for
            gaming brands.
          </p>
          <External href="https://space.bilibili.com/24903476">
            My Bilibili channel
          </External>
        </div>
      </div>
    </section>
    <Contact />
  </>
);

export const Contact: React.FC = () => (
  <section id="contact" className="contact" aria-labelledby="contact-title">
    <div className="shell contact-grid">
      <div>
        <h2 id="contact-title">Get in touch.</h2>
        <p>
          If my work fits something your team is doing, email is the fastest
          way to reach me.
        </p>
        <CopyEmail className="copy-email--large" />
        <div className="social-links">
          <External href="https://www.linkedin.com/in/max-w-zhang/">
            LinkedIn
          </External>
          <External href="https://github.com/maxz101524">GitHub</External>
        </div>
      </div>
      <div className="resume-list">
        <h3>Resumes</h3>
        <a href="/resumes/Max-Zhang-ML-AI.pdf" download>
          <span>
            Machine learning &amp; applied AI <small>PDF</small>
          </span>
          <Download size={17} aria-hidden="true" />
        </a>
        <a href="/resumes/Max-Zhang-Data-Science.pdf" download>
          <span>
            Data science &amp; analytics <small>PDF</small>
          </span>
          <Download size={17} aria-hidden="true" />
        </a>
      </div>
    </div>
  </section>
);

export const CaseStudy: React.FC = () => {
  const { id } = useParams();
  const index = projects.findIndex((x) => x.id === id);
  const p = projects[index];
  if (!p) return <NotFound />;
  return (
    <>
      <article className="case-study shell">
        <Link className="back-link" to="/#work">
          <ArrowLeft size={15} aria-hidden="true" />
          All work
        </Link>
        <header className="case-header">
          <p className="case-index">
            {String(index + 1).padStart(2, "0")}
            <span>Case study · {p.category}</span>
          </p>
          <h1>{p.title}</h1>
          <p className="case-summary">{p.summary}</p>
        </header>
        <section className="case-facts" aria-label="At a glance">
          <dl>
            <div>
              <dt>Role</dt>
              <dd>{p.role}</dd>
            </div>
            <div>
              <dt>When</dt>
              <dd>{p.period}</dd>
            </div>
            <div>
              <dt>Tools</dt>
              <dd>{p.stack.join(", ")}</dd>
            </div>
          </dl>
          <div className="case-results">
            <h2>Results</h2>
            <ul>
              {p.highlights.map((h) => (
                <li key={h}>
                  <Marked text={h} />
                </li>
              ))}
            </ul>
          </div>
        </section>
        {p.id === "smarteats" ? (
          <figure className="case-screenshot">
            <Screenshot alt="SmartEats home screen with menu, meal planner and profile features" />
            <figcaption>
              SmartEats application screenshot from the project archive.
            </figcaption>
          </figure>
        ) : (
          <div className="case-visual">{caseVisual[p.id]}</div>
        )}
        <div className="case-prose">
          {p.sections.map((s) => (
            <section key={s.title}>
              <h2>{s.title}</h2>
              <p>{s.text}</p>
            </section>
          ))}
        </div>
        {p.id === "cloudsway" && <QualityEvidence />}
        {p.id === "equota" && <ForecastEvidence />}
        {p.id === "smarteats" && (
          <>
            <Benchmark />
            <div className="case-links">
              <External href={repo}>View the project repository</External>
              <External href="https://smarteats.site">Open SmartEats</External>
            </div>
          </>
        )}
        <nav className="next-projects" aria-label="More work">
          <h2>More work</h2>
          {projects
            .filter((x) => x.id !== id)
            .map((x) => (
              <Link key={x.id} to={`/work/${x.id}`}>
                <span>
                  {x.name}
                  <small>{x.category}</small>
                </span>
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
            ))}
        </nav>
      </article>
      <Contact />
    </>
  );
};

export const NotFound: React.FC = () => (
  <section className="shell not-found">
    <p className="case-index">404</p>
    <h1>This page could not be found.</h1>
    <Link className="button" to="/">
      Back to the portfolio
    </Link>
  </section>
);

export const Footer: React.FC = () => (
  <footer className="shell site-footer">
    <span>© {new Date().getFullYear()} Max Zhang</span>
    <span className="colophon">
      Set in Hanken Grotesk and Instrument Serif
    </span>
    <a href="#top">Back to top ↑</a>
  </footer>
);
